import React, {useState, useCallback} from 'react';
import {TextField} from '@mui/material';
import {removeInitialSpecialChars} from '../../../../util/sanitizeUtil';
import {validateScacCode} from '../../../../util/ValidationUtils';
import {CheckInFieldProps} from '../interfaces';
import {CODE_INPUT_PROPS} from './keyboardInputProps';

const SCAC_ERROR_MESSAGE = 'SCAC code must be alphanumeric and be 2 to 4 characters long.';

/**
 * SCAC Code text field with built-in inline validation.
 * Uppercases and sanitizes input. Validates SCAC format on every keystroke and
 * manages its own inline error state. The parent's `error` prop (from submit-time
 * validation) is also displayed when present.
 */
const ScacCodeField: React.FC<CheckInFieldProps> = ({value, onValueChange, error, required}) => {
  const [inlineError, setInlineError] = useState<string | undefined>();

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = removeInitialSpecialChars(e.target.value.toUpperCase());

    if (sanitized && !validateScacCode(sanitized)) {
      setInlineError(SCAC_ERROR_MESSAGE);
    } else {
      setInlineError(undefined);
    }

    onValueChange('scacCode', sanitized);
  }, [onValueChange]);

  const displayError = error || inlineError;

  return (
    <TextField
      id="scacCode"
      name="scacCode"
      label="SCAC Code"
      value={value}
      onChange={handleChange}
      placeholder="Enter the SCAC code"
      variant="standard"
      fullWidth
      required={required}
      error={!!displayError}
      helperText={displayError}
      inputProps={{...CODE_INPUT_PROPS, 'aria-label': 'SCAC Code'}}
    />
  );
};

export default ScacCodeField;

